import axios from "axios";
import { Network } from "@capacitor/network";

let currentNetworkStatus = {
  connected: typeof navigator !== "undefined" ? navigator.onLine !== false : false,
  connectionType: "unknown",
};
let networkGuardInitialized = false;

const updateNetworkStatus = (status) => {
  if (!status || typeof status.connected !== "boolean") {
    return;
  }

  currentNetworkStatus = {
    connected: status.connected,
    connectionType: status.connectionType || "unknown",
  };

  console.debug("[networkGuard] network status updated:", currentNetworkStatus);
};

export const isAppOnline = () => {
  if (typeof window === "undefined") return true;

  const browserSaysOnline =
    typeof navigator === "undefined" ? undefined : navigator.onLine !== false;

  if (browserSaysOnline === true) {
    return true;
  }

  if (currentNetworkStatus.connected === true) {
    return true;
  }

  return false;
};

const buildOfflineError = () => {
  const error = new Error("No internet connection. Request blocked while offline.");
  error.name = "NetworkError";
  error.code = "NETWORK_ERROR";
  return error;
};

const reportNetworkError = (error) => {
  if (!isAppOnline()) {
    return;
  }

  const isNetworkError =
    error?.code === "ERR_NETWORK" ||
    error?.name === "NetworkError" ||
    error?.message?.toLowerCase().includes("failed to fetch") ||
    error?.message?.toLowerCase().includes("network error") ||
    error?.message?.toLowerCase().includes("net::err_address_unreachable");

  if (isNetworkError) {
    if (typeof navigator !== "undefined" && navigator.onLine !== false) {
      return;
    }

    updateNetworkStatus({ connected: false, connectionType: "none" });
  }
};

const patchFetch = () => {
  if (typeof window === "undefined" || !window.fetch) {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    if (!isAppOnline()) {
      return Promise.reject(buildOfflineError());
    }

    try {
      return await originalFetch(...args);
    } catch (error) {
      reportNetworkError(error);
      throw error;
    }
  };
};

const patchXMLHttpRequest = () => {
  if (typeof window === "undefined" || typeof window.XMLHttpRequest === "undefined") {
    return;
  }

  const originalSend = window.XMLHttpRequest.prototype.send;
  window.XMLHttpRequest.prototype.send = function (...args) {
    if (!isAppOnline()) {
      throw buildOfflineError();
    }
    return originalSend.apply(this, args);
  };
};

const patchWebSocket = () => {
  if (typeof window === "undefined" || typeof window.WebSocket === "undefined") {
    return;
  }

  const NativeWebSocket = window.WebSocket;
  window.WebSocket = function (url, protocols) {
    if (!isAppOnline()) {
      throw buildOfflineError();
    }
    return new NativeWebSocket(url, protocols);
  };
  window.WebSocket.prototype = NativeWebSocket.prototype;
  window.WebSocket.CONNECTING = NativeWebSocket.CONNECTING;
  window.WebSocket.OPEN = NativeWebSocket.OPEN;
  window.WebSocket.CLOSING = NativeWebSocket.CLOSING;
  window.WebSocket.CLOSED = NativeWebSocket.CLOSED;
};

export const initNetworkGuard = async () => {
  if (typeof window === "undefined" || networkGuardInitialized) {
    return;
  }

  networkGuardInitialized = true;

  patchFetch();
  patchXMLHttpRequest();
  patchWebSocket();

  const syncStatus = async () => {
    try {
      const status = await Network.getStatus();
      updateNetworkStatus(status);
    } catch (error) {
      console.warn("[networkGuard] failed to read Capacitor network status:", error);
      if (typeof navigator !== "undefined") {
        updateNetworkStatus({
          connected: navigator.onLine !== false,
          connectionType: "unknown",
        });
      }
    }
  };

  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      updateNetworkStatus({ connected: true, connectionType: "unknown" });
    });
    window.addEventListener("offline", () => {
      updateNetworkStatus({ connected: false, connectionType: "none" });
    });
  }

  axios.interceptors.request.use(
    (config) => {
      if (!isAppOnline()) {
        return Promise.reject(buildOfflineError());
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      reportNetworkError(error);
      return Promise.reject(error);
    },
  );

  await syncStatus();
};
